import React from 'react'
import styled, { css } from 'styled-components'
import {
    DEFAULT_PLANS,
    DEFAULT_PLANS_PROD,
} from '~common/payment-processing/constants'
import { LoadingIndicator } from '../loading'
import { Icon } from '../icon'
import { Card } from '../card'
import {
    animationEase,
    animationFast,
    animationNormal,
    animationSlow,
    reducedMotion,
    fadeSlideUp,
    fadeIn,
    scaleFadeIn,
} from '~common/utils/animations'

interface PricingDisplayProps {
    isDev?: boolean
    selectPackage?: (packageId: string) => void
    isUpgrading?: string
    discountBannerText?: string
}

export type Feature = {
    name: string
    soon?: boolean
    icon?: string
}

const features: Feature[] = [
    { name: 'Scan Gmail for Invoices' },
    { name: 'Export to Google Drive & Sheets' },
    { name: 'Auto-Matching Bank Transactions', icon: 'match' },
    { name: 'Scan Online Portals', icon: 'categories' },
    { name: 'Live Chat Support' },
    { name: 'Credits never expire' },
    {
        name: 'Export to Accounting Software',
        soon: true,
        icon: 'accounting',
    },
    { name: 'Auto-Booking Transactions', soon: true, icon: 'booking' },
    { name: 'Custom Processing Rules', soon: true, icon: 'settings' },
]

export default function PricingDisplay(props: PricingDisplayProps) {
    const packages = props.isDev
        ? DEFAULT_PLANS['launch-price']
        : DEFAULT_PLANS_PROD['launch-price']

    return (
        <PricingGrid>
            <GlowWrapper>
                <GlowEffect />
                <GlowCard>
                    <GlowInner>
                        <Left>
                            <PlanList>
                                <OptionsBox>
                                    {packages?.variants.map((pkg) => {
                                        let invoiceText =
                                            (
                                                pkg?.limits
                                                    ?.maxInvoicesIngested ??
                                                0 * 2
                                            ).toString() + ' invoices'

                                        if (pkg.id === 'lifetime') {
                                            invoiceText = 'Lifetime Unlimited'
                                        }
                                        if (pkg.id === 'free') {
                                            invoiceText =
                                                (pkg?.limits?.maxInvoicesIngested?.toString() ??
                                                    0) + ' invoices'
                                        }

                                        return (
                                            <PlanOption
                                                key={pkg.id}
                                                onClick={() =>
                                                    props.selectPackage?.(
                                                        pkg.id,
                                                    )
                                                }
                                                role="button"
                                                tabIndex={0}
                                                offer={pkg.id === 'lifetime'}
                                            >
                                                {props.isUpgrading ===
                                                pkg.id ? (
                                                    <LoadingIndicator size="small" />
                                                ) : (
                                                    <>
                                                        <MainText>
                                                            <PlanOptionLeft>
                                                                <InvoiceText
                                                                    offer={
                                                                        pkg.id ===
                                                                        'lifetime'
                                                                    }
                                                                >
                                                                    {
                                                                        invoiceText
                                                                    }
                                                                </InvoiceText>
                                                            </PlanOptionLeft>
                                                            <PlanOptionRight>
                                                                {pkg?.price &&
                                                                pkg.price >
                                                                    0 ? (
                                                                    <Currency
                                                                        offer={
                                                                            pkg.id ===
                                                                            'lifetime'
                                                                        }
                                                                    >
                                                                        {' '}
                                                                        ${' '}
                                                                    </Currency>
                                                                ) : (
                                                                    ''
                                                                )}
                                                                <span>
                                                                    {pkg?.price &&
                                                                    pkg.price >
                                                                        0
                                                                        ? `${pkg?.price}`
                                                                        : 'Free'}
                                                                </span>
                                                            </PlanOptionRight>
                                                        </MainText>
                                                        {pkg.id ===
                                                            'lifetime' && (
                                                            <DiscountText>
                                                                🎉{' '}
                                                                {
                                                                    props.discountBannerText
                                                                }{' '}
                                                                🎉
                                                            </DiscountText>
                                                        )}
                                                    </>
                                                )}
                                            </PlanOption>
                                        )
                                    })}
                                </OptionsBox>
                            </PlanList>
                        </Left>
                        <Right>
                            <FeatureList>
                                {features
                                    .filter((f) => !f.soon)
                                    .map((f, i) => (
                                        <Feature key={i}>
                                            <Icon name="check" size="20px" />
                                            <FeatureText>{f.name}</FeatureText>
                                        </Feature>
                                    ))}
                            </FeatureList>

                            {features.some((f) => f.soon) && (
                                <RoadmapSection>
                                    <RoadmapTitle>Roadmap</RoadmapTitle>
                                    <RoadmapList>
                                        {features
                                            .filter((f) => f.soon)
                                            .map((f, i) => (
                                                <Feature
                                                    key={`roadmap-${i}`}
                                                    roadmap={true}
                                                >
                                                    <FeatureText roadmap>
                                                        {f.name}
                                                    </FeatureText>
                                                </Feature>
                                            ))}
                                    </RoadmapList>
                                </RoadmapSection>
                            )}
                        </Right>
                    </GlowInner>
                </GlowCard>
            </GlowWrapper>
        </PricingGrid>
    )
}

const GlowWrapper = styled.div`
    position: relative;
    @media (max-width: 768px) {
        width: 100%;
    }
`

// @ts-expect-error styled-components attrs typing
const GlowEffect = styled.div.attrs({ 'data-animate': true })`
    position: absolute;
    top: 0;
    left: -48px;
    right: -48px;
    bottom: -5%;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    pointer-events: none;
    z-index: -1;
    background:
        radial-gradient(
            1200px 1820px at 18% 118%,
            ${({ theme }) => theme.colors.warning + '56'} 0%,
            rgba(255, 219, 86, 0) 60%
        ),
        radial-gradient(
            1400px 1520px at 50% 132%,
            rgba(56, 189, 248, 0.4) 0%,
            rgba(56, 189, 248, 0) 60%
        ),
        radial-gradient(
            1200px 2220px at 82% 118%,
            ${({ theme }) => theme.colors.info + '66'} 0%,
            rgba(146, 191, 255, 0) 60%
        );
    filter: blur(20px);
    opacity: 0.85;
    will-change: opacity, transform;
    animation: ${fadeIn} ${animationSlow} ${animationEase} both;
    animation-delay: 120ms;
    animation-play-state: paused;
    ${reducedMotion}

    @media (max-width: 768px) {
        display: none;
    }
`
const PricingGrid = styled.div`
    margin: ${({ theme }) => theme.spacing[6]} auto;
`
const GlowCard = styled(Card)`
    border: 1px solid ${({ theme }) => theme.colors.border.tertiary};
    padding: ${({ theme }) => theme.spacing[6]};
    position: relative;
    z-index: 2;
    isolation: isolate;
    overflow: visible;
`
const GlowInner = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${({ theme }) => theme.spacing[16]};
    padding: ${({ theme }) => theme.spacing[4]};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
`

const Left = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    flex: 1;
`
const Right = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    flex: 1;
`

const PlanList = styled.div`
    display: grid;
    gap: ${({ theme }) => theme.spacing[2]};
    width: 100%;
`

const OptionsBox = styled.div`
    display: grid;
    border: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    border-radius: ${({ theme }) => theme.borderRadius.md};
`

const PlanOptionLeft = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    white-space: nowrap;
`

const PlanOptionRight = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
`

const PlanOption = styled.div<{ offer?: boolean; hasOnClick?: boolean }>`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[4]};
    min-height: 60px;
    //prettier-ignore
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) =>
        theme.spacing[3]};
    border-bottom: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    cursor: pointer;
    transition: background ${({ theme }) => theme.transitions.fast};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    box-sizing: border-box;
    ${({ hasOnClick }) =>
        hasOnClick &&
        css`
            &:hover {
                background: ${({ theme }) => theme.colors.greyScale[400]};
            }
        `}

    &:last-child {
        border-bottom: none;
    }

    ${({ offer }) =>
        offer &&
        css`
            background: linear-gradient(
                135deg,
                ${({ theme }) => theme.colors.info} 0%,
                ${({ theme }) => theme.colors.warning} 100%
            );
            color: black;
            /* prettier-ignore */
            border-radius: 0 0 ${({ theme }) => theme.borderRadius.md} ${({
                theme,
            }) => theme.borderRadius.md};

            &:hover {
                opacity: 0.8;
                background: linear-gradient(
                    135deg,
                    ${({ theme }) => theme.colors.info} 0%,
                    ${({ theme }) => theme.colors.warning} 100%
                );
            }
        `}
`

const FeatureList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[3]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const Feature = styled.li<{ roadmap?: boolean }>`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[3]};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};

    ${({ roadmap }) =>
        roadmap &&
        css`
            justify-content: center;
        `}
`

const FeatureText = styled.span<{ roadmap?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    white-space: nowrap;
    gap: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};

    ${({ roadmap }) =>
        roadmap &&
        css`
            font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
            text-align: right;
            line-height: 1.5;
            justify-content: center;
        `}
`

const RoadmapSection = styled.div`
    margin-top: ${({ theme }) => theme.spacing[4]};
    padding-top: ${({ theme }) => theme.spacing[4]};
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
`

const RoadmapTitle = styled.h4`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    color: ${({ theme }) => theme.colors.text.tertiary};
    margin: 0 0 ${({ theme }) => theme.spacing[3]} 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`

const RoadmapList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`

// Removed RadioRoot as radios are no longer used

const Currency = styled.span<{ offer?: boolean }>`
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    color: ${({ theme }) => theme.colors.text.tertiary};
    margin-right: ${({ theme }) => theme.spacing[1]};

    ${({ offer }) =>
        offer &&
        css`
            color: black;
        `}
`

const DiscountText = styled.span`
    color: ${({ theme }) => theme.colors.greyScale[1000]};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
`

const InvoiceTotal = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[2]};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
`

const CountDownText = styled.span`
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.info};
`

const Strikethrough = styled.span`
    position: relative;
    color: ${({ theme }) => theme.colors.info};
    margin-right: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    white-space: nowrap;
    /* 
    &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 2px;
        background-color: ${({ theme }) => theme.colors.error};
        opacity: 0.5;
        transform: translateY(-50%) rotate(-5deg);
        transform-origin: center;
    } */
`

const InvoiceText = styled.span<{ offer?: boolean }>`
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.info};

    ${({ offer }) =>
        offer &&
        css`
            color: black;
        `}
`

const CalendlySection = styled.div`
    margin: ${({ theme }) => theme.spacing[8]} 0;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
`

const MainText = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`

const SupportTitle = styled.h3`
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0 0 ${({ theme }) => theme.spacing[4]} 0;
    text-align: center;
`

const MoreQuestionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    margin-top: ${({ theme }) => theme.spacing[10]};
`
